import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  User as FirebaseUser,
  UserCredential,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut
} from '@angular/fire/auth';
import { BehaviorSubject, Observable, from, map, switchMap, tap, throwError, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppUser } from '../users/user.model';

export type AuthFlowStage = 'authenticated' | 'otp_required' | 'email_verification_required';

export interface AuthFlowResult {
  stage: AuthFlowStage;
  user?: AppUser;
  email?: string;
  message?: string;
  maskedEmail?: string;
  expiresAt?: string;
  resendAvailableAt?: string;
}

interface OtpChallengeResponse {
  message: string;
  maskedEmail: string;
  expiresAt: string;
  resendAvailableAt: string;
}

interface OtpVerifyResponse {
  message: string;
  verifiedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private readonly APP_USER_KEY = 'appUser';
  private readonly ID_TOKEN_KEY = 'idToken';
  private readonly authApiUrl = `${environment.restApiUrl}auth`;

  private currentUserSubject = new BehaviorSubject<AppUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$: Observable<boolean> = this.currentUser$.pipe(map((user) => !!user));
  public currentUserId$: Observable<number | null> = this.currentUser$.pipe(map((user) => user?.id || null));
  public currentFirebaseUserId$: Observable<string | null> = this.currentUser$.pipe(map((user) => user?.firebaseId || null));

  constructor(
    private auth: Auth,
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.currentUserSubject.next(this.getStoredAppUser());
    }

    onAuthStateChanged(this.auth, async (firebaseUser: FirebaseUser | null) => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      if (!firebaseUser) {
        this.clearLocalSession();
        return;
      }

      try {
        await this.storeIdToken(firebaseUser);
      } catch (error) {
        console.error('Error refreshing Firebase ID token on auth state change:', error);
        this.clearLocalSession();
        return;
      }

      if (this.currentUserSubject.getValue()) {
        this.fetchAuthorizedAppUser(firebaseUser).subscribe({
          error: (error) => {
            console.error('Error restoring authorized session after auth state change:', error);
            this.clearLocalSession();
          }
        });
      }
    });
  }

  getAuthState(): Observable<AppUser | null> {
    return this.currentUser$;
  }

  signInWithEmailPassword(email: string, password: string): Observable<AuthFlowResult> {
    return from(signInWithEmailAndPassword(this.auth, email, password)).pipe(
      switchMap((credential) => this.prepareExternalLoginSession(credential.user, true)),
      catchError((error) => this.handlePostSignInError(error))
    );
  }

  signUpWithEmailPassword(email: string, password: string): Observable<AuthFlowResult> {
    return from(createUserWithEmailAndPassword(this.auth, email, password)).pipe(
      switchMap(async (credential: UserCredential) => {
        if (!credential.user) {
          throw new Error('Firebase sign-up succeeded but no user object was returned.');
        }

        await this.storeIdToken(credential.user, true);
        await sendEmailVerification(credential.user);
        return credential.user;
      }),
      switchMap(() => this.signOutFirebaseOnly()),
      map(() => ({
        stage: 'email_verification_required' as const,
        email,
        message: 'Your password account has been created. Please verify your email before signing in.'
      }))
    );
  }

  signInWithGoogle(): Observable<AuthFlowResult> {
    return this.performPopupSignIn(new GoogleAuthProvider(), 'external').pipe(
      catchError((error) => this.handlePostSignInError(error))
    );
  }

  signInWithMicrosoft(): Observable<AuthFlowResult> {
    return this.performPopupSignIn(new OAuthProvider('microsoft.com'), 'external').pipe(
      catchError((error) => this.handlePostSignInError(error))
    );
  }

  signInWithSaml(): Observable<AuthFlowResult> {
    return this.performPopupSignIn(new SAMLAuthProvider('saml.tatc-digital-hub'), 'internal').pipe(
      catchError((error) => this.handlePostSignInError(error))
    );
  }

  resendSupplierOtp(): Observable<AuthFlowResult> {
    const firebaseUser = this.getCurrentFirebaseUser();
    if (!firebaseUser) {
      return throwError(() => new Error('No Firebase user is available for OTP resend.'));
    }

    return from(this.storeIdToken(firebaseUser, true)).pipe(
      switchMap(() => this.http.post<OtpChallengeResponse>(`${this.authApiUrl}/external/otp/challenge`, {})),
      map((response) => ({
        stage: 'otp_required' as const,
        email: firebaseUser.email ?? '',
        message: response.message,
        maskedEmail: response.maskedEmail,
        expiresAt: response.expiresAt,
        resendAvailableAt: response.resendAvailableAt
      }))
    );
  }

  verifySupplierOtp(otp: string): Observable<AppUser> {
    const firebaseUser = this.getCurrentFirebaseUser();
    if (!firebaseUser) {
      return throwError(() => new Error('No Firebase user is available for OTP verification.'));
    }

    return from(this.storeIdToken(firebaseUser, true)).pipe(
      switchMap(() =>
        this.http.post<OtpVerifyResponse>(`${this.authApiUrl}/external/otp/verify`, { otp })
      ),
      switchMap(() => this.fetchAuthorizedAppUser(firebaseUser))
    );
  }

  signOut(): Observable<void> {
    return from(signOut(this.auth)).pipe(
      tap(() => this.clearLocalSession())
    );
  }

  getCurrentAppUser(): AppUser | null {
    return this.currentUserSubject.getValue();
  }

  getCurrentFirebaseUser(): FirebaseUser | null {
    return this.auth.currentUser;
  }

  getStoredAppUser(): AppUser | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const storedAppUser = sessionStorage.getItem(this.APP_USER_KEY);
    try {
      return storedAppUser ? JSON.parse(storedAppUser) : null;
    } catch (e) {
      console.error('Error parsing stored AppUser from session storage', e);
      sessionStorage.removeItem(this.APP_USER_KEY);
      return null;
    }
  }

  async getIdToken(): Promise<string | null> {
    const firebaseUser = this.getCurrentFirebaseUser();
    if (!firebaseUser) {
      return null;
    }

    try {
      return await this.storeIdToken(firebaseUser, true);
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  getStoredIdToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return sessionStorage.getItem(this.ID_TOKEN_KEY);
  }

  sendPasswordResetEmail(email: string): Observable<void> {
    return from(firebaseSendPasswordResetEmail(this.auth, email));
  }

  private performPopupSignIn(
    provider: GoogleAuthProvider | OAuthProvider | SAMLAuthProvider,
    flow: 'internal' | 'external'
  ): Observable<AuthFlowResult> {
    return from(signInWithPopup(this.auth, provider)).pipe(
      switchMap((credential: UserCredential) => {
        const firebaseUser = credential.user;
        if (!firebaseUser) {
          throw new Error('Firebase sign-in succeeded but no user object was returned.');
        }

        return flow === 'internal'
          ? this.completeInternalLogin(firebaseUser)
          : this.prepareExternalLoginSession(firebaseUser, false);
      })
    );
  }

  private completeInternalLogin(firebaseUser: FirebaseUser): Observable<AuthFlowResult> {
    return from(this.storeIdToken(firebaseUser, true)).pipe(
      switchMap(() => this.fetchAuthorizedAppUser(firebaseUser)),
      map((user) => ({
        stage: 'authenticated' as const,
        user
      })),
      switchMap((result) => this.completeAuthFlow(result))
    );
  }

  private prepareExternalLoginSession(
    firebaseUser: FirebaseUser,
    requireEmailVerification: boolean
  ): Observable<AuthFlowResult> {
    return from(this.storeIdToken(firebaseUser, true)).pipe(
      switchMap(() => {
        if (requireEmailVerification && !firebaseUser.emailVerified) {
          return this.signOutFirebaseOnly().pipe(
            map(() => ({
              stage: 'email_verification_required' as const,
              email: firebaseUser.email ?? '',
              message: 'Please verify your email address before signing in.'
            }))
          );
        }

        return this.http.post<OtpChallengeResponse>(`${this.authApiUrl}/external/otp/challenge`, {}).pipe(
          map((response) => ({
            stage: 'otp_required' as const,
            email: firebaseUser.email ?? '',
            message: response.message,
            maskedEmail: response.maskedEmail,
            expiresAt: response.expiresAt,
            resendAvailableAt: response.resendAvailableAt
          }))
        );
      }),
      switchMap((result) => this.completeAuthFlow(result))
    );
  }

  private fetchAuthorizedAppUser(firebaseUser: FirebaseUser): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.authApiUrl}/me`).pipe(
      tap((appUser) => this.setAuthorizedAppUser(appUser, firebaseUser))
    );
  }

  private setAuthorizedAppUser(appUser: AppUser, firebaseUser: FirebaseUser): void {
    const enrichedUser: AppUser = {
      ...appUser,
      firebaseId: firebaseUser.uid,
      email: firebaseUser.email ?? appUser.email,
      profilePictureUrl: firebaseUser.photoURL ?? appUser.profilePictureUrl ?? 'assets/manager.png',
      mobileNumber: firebaseUser.phoneNumber ?? appUser.mobileNumber
    };

    this.currentUserSubject.next(enrichedUser);

    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this.APP_USER_KEY, JSON.stringify(enrichedUser));
    }
  }

  private async storeIdToken(firebaseUser: FirebaseUser, forceRefresh = false): Promise<string> {
    const idToken = await firebaseUser.getIdToken(forceRefresh);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this.ID_TOKEN_KEY, idToken);
    }
    return idToken;
  }

  private clearLocalSession(): void {
    this.currentUserSubject.next(null);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(this.APP_USER_KEY);
      sessionStorage.removeItem(this.ID_TOKEN_KEY);
    }
  }

  private signOutFirebaseOnly(): Observable<void> {
    return from(signOut(this.auth)).pipe(
      tap(() => this.clearLocalSession())
    );
  }

  private completeAuthFlow(result: AuthFlowResult): Observable<AuthFlowResult> {
    return of(result);
  }

  private handlePostSignInError(error: unknown): Observable<never> {
    const firebaseUser = this.getCurrentFirebaseUser();
    if (!firebaseUser) {
      return throwError(() => error);
    }

    return this.signOutFirebaseOnly().pipe(
      switchMap(() => throwError(() => error))
    );
  }
}
